import React, { useState } from "react";

import { Box, Button, FormControl, IconButton, MenuItem, Select, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ClearIcon } from "@mui/x-date-pickers";
import useLoadMore from "hooks/useLoadMore";
import SearchBox from "components/SearchBox";
import ButtonComponent from "components/button/ButtonComponent";
import ExportButton from "components/button/ExportButton";
import CategoryTable from "../components/CategoryTable";
import { Category } from "../types";
import useCategoryList from "../hooks/useCategoryList";
import CategoryFormModal from "../components/CategoryFormModal";

const styles = {
  textButton: {
    fontSize: { xs: "16px", md: "20px", xl: "25px" },
  },
  button: {
    width: 190,
    marginX: "auto",
    marginTop: 5,
  },
};

/**
 * UserList component displays a list of users with pagination and filtering options.
 * It includes a search form, category filters, and a table to display user details.
 * @example
 * return (
 *   <UserList />
 * )
 * @returns {JSX.Element} The rendered component.
 * @remarks
 * - The component uses infinite scrolling to load more users as the user scrolls down.
 * - It includes a modal to display detailed information about a selected user.
 * - The search form allows filtering users by name, email, and type ID.
 * - The component uses Material-UI for styling and layout.
 * @event handleDetailClick - Opens the user detail modal with the selected user's information.
 * @event handleCloseModal - Closes the user detail modal and clears the selected user.
 * @event handleCheckbox - Updates the state of the "Available ID only" checkbox.
 * @event handleScroll - Handles the infinite scrolling to load more users.
 */
function CategoryList() {
  const navigate = useNavigate();
  const {
    categories,
    queryParams,
    groups,
    loading,
    total,
    handleSearch,
    setQueryParams,
    onLoadMore,
  } = useCategoryList();
  const { renderNodeLoader } = useLoadMore(onLoadMore, loading);

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [isShowModal, setIsShowModal] = useState({ create: false, update: false });

  const handleSelectCategory = (e: any) => {
    const code = e.target.value;
    if (code) {
      setSelectedCategory(code);
      setQueryParams((prev) => ({
        ...prev,
        categoryKbn: code as string,
      }));
    }
  };

  const handleClear = () => {
    if (selectedCategory) {
      setSelectedCategory(null);
      setQueryParams((prev) => ({
        ...prev,
        categoryKbn: "" as string,
      }));
    }
  };

  const openUpdateModal = (category: Category) => {
    setSelectedCategory(() => category);
    setIsShowModal((prev) => ({ ...prev, update: true }));
  };

  const closeUpdateModel = () => {
    setSelectedCategory(null);
    setIsShowModal((prev) => ({ ...prev, update: false }));
  };

  const handleCloseModal = () => {
    setIsShowModal((prev) => ({ ...prev, create: false, update: false }));
    setSelectedCategory(null);
  };

  const onUpdatedCategory = () => {
    closeUpdateModel();
    handleSearch();
  };

  const onCreatedCategory = () => {
    setIsShowModal((prev) => ({ ...prev, create: false }));
    handleSearch();
  };

  return (
    <>
      <Box sx={{ px: { xs: 2.5, md: 3, lg: "60px" }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", px: { xs: 2.5, md: 3, lg: "60px" } }}>
        <Box
          sx={{
            mt: { xs: 3, md: 4, lg: "46px" },
            display: "flex",
            flexDirection: "column",
            gap: 1,
            alignItems: "flex-start",
            width: { xs: "100%", lg: "15%" },
          }}
        >
          <Typography
            variant="h4"
            margin={0}
            textAlign="left"
            sx={{ fontSize: { xs: 24, lg: 33 }, color: "var(--gray-700)" }}
          >
            マスタ管理
          </Typography>
          <Typography
            variant="h5"
            margin={0}
            textAlign="left"
            sx={{ fontSize: { xs: 24, lg: 28 }, color: "var(--gray-700)", whiteSpace: "nowrap" }}
          >
            分類マスタ
          </Typography>
          <ExportButton
            sx={{ fontSize: { xs: 16, md: 20, lg: 23 }, width: 180 }}
            label="新規登録"
            onClick={() => setIsShowModal((prev) => ({ ...prev, create: true }))}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            mt: { xs: 3, md: 4, lg: 9 },
            mb: 2,
          }}
        >
          <SearchBox
            title="区分"
            id="category_kbn"
            placeholder=""
            sxLabel={{ width: { xs: 200, lg: 135 }, maxWidth: 200 }}
            sx={{ minWidth: 0, width: { xs: "100%", lg: "auto" } }}
            fieldWidth="100%"
          >
            <FormControl
              sx={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                width: "100%",
                flexDirection: "row",
              }}
            >
              <Select
                id="category_kbn"
                value={queryParams.categoryKbn}
                onChange={(e) => handleSelectCategory(e)}
                sx={{
                  "& .MuiSelect-select": { padding: "12.5px 14px" },
                  minWidth: { xs: "auto", lg: 250 },
                  width: { xs: "100%", lg: "auto" },
                  height: "44px",
                  fontSize: { xs: 16, md: 18, lg: 20 },
                }}
                displayEmpty
                renderValue={(selected: string) => {
                  if (!selected) {
                    return <span style={{ color: 'var(--gray-100)', fontSize: '16px' }}>全て</span>;
                  }
                  return groups.listKbn.find((kbn) => kbn === selected) || null;
                }
                }
              >
                {groups.listKbn.map((item) => (
                  <MenuItem value={item} key={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
              {selectedCategory && (
                <IconButton onClick={handleClear} size="small">
                  <ClearIcon />
                </IconButton>
              )}
            </FormControl>
          </SearchBox>
          <ButtonComponent
            theme="dark"
            onClick={handleSearch}
            label="検索"
            sx={{ ...styles.button, ...styles.textButton }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          backgroundColor: "var(--offwhite-100)",
          p: 2.5,
        }}
      >
        <Box
          sx={{
            width: { lg: "15%" },
            display: { xs: "none", lg: "block" },
          }}
        >
          &nbsp;
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          paddingBottom="20px"
          flex={1}
          sx={{ width: "100%" }}
        >
          <Typography
            fontSize="20px"
            sx={{ textAlign: "right", mr: 7, mb: 1, minWidth: { xs: "100%", lg: 1218 } }}
          >
            {`件数【${total}件】`}
          </Typography>
          <Box sx={{ overflowX: "auto", width: { xs: "100%", lg: "auto" } }}>
            <CategoryTable total={total} categories={categories} openModal={openUpdateModal} />
            {renderNodeLoader()}
          </Box>
        </Box>
      </Box>

      {selectedCategory && (
        <CategoryFormModal
          type="update"
          visible={isShowModal.update}
          onClose={handleCloseModal}
          onSuccess={onUpdatedCategory}
          categoryDetail={selectedCategory}
          isLoading={loading}
        />
      )}

      <CategoryFormModal
        type="create"
        visible={isShowModal.create}
        onClose={handleCloseModal}
        onSuccess={onCreatedCategory}
        isLoading={loading}
      />
    </>
  );
}

export default CategoryList;
